import { mountToAppShell } from '../appShell'
import { NeonColors, FONTS, neonTitleGlow } from '../neon-theme'
import { NetworkManager } from '../../network/NetworkManager'
import {
  buildCardStates,
  initialDailyRewardsState,
  reduceDailyRewards,
  type CardState,
  type DailyRewardsAction,
  type DailyRewardsState,
  type DailyRewardStatus,
} from './dailyRewardsState'

/**
 * SNAKE-17: 7-day daily reward screen with grace period.
 *
 * Behaviour matches GDD §10:
 *   - On mount: GET /api/profile/daily-reward → render 7-card grid.
 *   - Claim button POSTs the same endpoint, surfaces the new status without a
 *     second GET. Race-loss (Already claimed today + data.status) silently
 *     refreshes via claim_already.
 *   - Banners: resetRequired → "С возвращением!"; graceWindow → "Streak
 *     сохранён!"; wrap after Day 7 → "Неделя пройдена!".
 *   - Countdown timer (ЧЧ:ММ) ticks every 60s when canClaim=false.
 *   - No auth token → unauthenticated message, no fetch.
 */

interface ApiStatusResponse {
  success: boolean
  data?: DailyRewardStatus
}

interface ApiClaimResponse {
  success: boolean
  error?: string
  data?: {
    reward?: number
    newDailyStreak?: number
    status?: DailyRewardStatus
  }
}

export interface DailyRewardsScreenOptions {
  onClaimed?: () => void | Promise<void>
}

export class DailyRewardsScreen {
  private container: HTMLDivElement
  private state: DailyRewardsState = initialDailyRewardsState
  private onClose: (() => void) | null = null
  private options: DailyRewardsScreenOptions = {}

  // Tick the countdown banner every 60s while canClaim is false.
  private countdownTimer: number | null = null

  // Guard against double-clicks on the claim button.
  private claimBusy = false

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'daily-rewards-screen'
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center;
      background: rgba(13,13,26,0.96); z-index: 210;
      font-family: ${FONTS.body}; color: #fff;
      overflow-y: auto; padding: 28px 0;
    `
  }

  show(onClose: () => void, options: DailyRewardsScreenOptions = {}): void {
    this.onClose = onClose
    this.options = options
    this.state = initialDailyRewardsState

    const token = NetworkManager.getInstance().getToken() ?? null
    if (!token) {
      this.dispatch({ type: 'load_unauth' })
      mountToAppShell(this.container)
      return
    }

    this.render()
    mountToAppShell(this.container)
    void this.fetchStatus()
  }

  hide(): void {
    this.stopCountdownTimer()
    this.container.remove()
  }

  dispose(): void {
    this.stopCountdownTimer()
    this.hide()
    this.onClose = null
    this.options = {}
  }

  // -----------------------------------------------------------------------
  // Networking
  // -----------------------------------------------------------------------

  private getToken(): string | null {
    return NetworkManager.getInstance().getToken() ?? null
  }

  private async fetchStatus(): Promise<void> {
    const token = this.getToken()
    if (!token) {
      this.dispatch({ type: 'load_unauth' })
      return
    }

    try {
      const res = await fetch('/api/profile/daily-reward', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as ApiStatusResponse
      if (data?.success && data.data) {
        this.dispatch({ type: 'load_ok', status: data.data })
      } else {
        this.dispatch({ type: 'load_err' })
      }
    } catch {
      this.dispatch({ type: 'load_err' })
    }
  }

  private async doClaim(): Promise<void> {
    if (this.claimBusy) return
    const token = this.getToken()
    if (!token) return
    if (this.state.kind !== 'loaded') return

    this.claimBusy = true
    this.dispatch({ type: 'claim_start' })
    try {
      const res = await fetch('/api/profile/daily-reward', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = (await res.json().catch(() => null)) as ApiClaimResponse | null

      if (data?.success && data.data?.status && typeof data.data.reward === 'number') {
        this.dispatch({
          type: 'claim_ok',
          status: data.data.status,
          reward: data.data.reward,
        })
        void this.options.onClaimed?.()
        return
      }

      // Race: server says "already claimed today" but kindly returns current
      // status so we can resync the grid without a noisy toast.
      if (
        !data?.success &&
        data?.error === 'Already claimed today' &&
        data.data?.status
      ) {
        this.dispatch({ type: 'claim_already', status: data.data.status })
        return
      }

      // Real failure path. Reset to loaded (so the user sees the grid again)
      // and refresh status defensively in case server state moved.
      this.dispatch({ type: 'claim_err' })
      void this.fetchStatus()
    } catch {
      this.dispatch({ type: 'claim_err' })
      void this.fetchStatus()
    } finally {
      this.claimBusy = false
    }
  }

  private dispatch(action: DailyRewardsAction): void {
    this.state = reduceDailyRewards(this.state, action)
    this.render()
    this.syncCountdownTimer()
  }

  // -----------------------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------------------

  private clear(): void {
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild)
    }
  }

  private render(): void {
    this.clear()

    if (this.state.kind === 'unauthenticated') {
      this.renderUnauthenticated()
      return
    }
    if (this.state.kind === 'loading') {
      this.renderLoading()
      return
    }
    if (this.state.kind === 'error') {
      this.renderError()
      return
    }

    // loaded or claiming
    const status = this.state.status
    const isClaiming = this.state.kind === 'claiming'
    const wrap = this.makeShell()

    if (status.activeStreakDays > 0) {
      wrap.appendChild(this.makeStreakIndicator(status.activeStreakDays))
    }

    // Banners — at most one shown at a time.
    if (status.resetRequired) {
      wrap.appendChild(
        this.makeBanner(
          status.previousStreakDays > 0
            ? `С возвращением! Был streak ${status.previousStreakDays} дней — начнём заново 🌱`
            : 'С возвращением! Начинаем заново 🌱',
          NeonColors.info,
        ),
      )
    } else if (status.graceWindow && status.canClaim) {
      wrap.appendChild(
        this.makeBanner('Streak сохранён! Не теряй темп 🔥', NeonColors.success),
      )
    } else if (
      status.streakContinues &&
      status.canClaim &&
      status.nextClaimDayIndex === 0 &&
      status.activeStreakDays > 0
    ) {
      wrap.appendChild(
        this.makeBanner('Неделя пройдена! Начинаем новый цикл 🎉', NeonColors.gold),
      )
    }

    wrap.appendChild(this.makeGrid(status))

    if (status.canClaim) {
      wrap.appendChild(this.makeClaimButton(status.nextReward, isClaiming))
    } else {
      wrap.appendChild(this.makeCountdown(status.nextRewardAvailableAt))
    }

    wrap.appendChild(this.makeReturnButton())
    this.container.appendChild(wrap)
  }

  private renderLoading(): void {
    const wrap = this.makeShell()
    const msg = document.createElement('div')
    msg.style.cssText = `text-align: center; color: ${NeonColors.textMuted}; margin: 24px 0;`
    msg.textContent = 'Загрузка...'
    wrap.appendChild(msg)
    this.container.appendChild(wrap)
  }

  private renderError(): void {
    const wrap = this.makeShell()
    const msg = document.createElement('div')
    msg.style.cssText = `text-align: center; color: ${NeonColors.danger}; margin: 24px 0;`
    msg.textContent = 'Не удалось загрузить. Попробуйте ещё раз.'
    wrap.appendChild(msg)

    const retry = document.createElement('button')
    retry.type = 'button'
    retry.style.cssText = this.primaryButtonCss()
    retry.textContent = 'Повторить'
    retry.addEventListener('click', () => {
      this.dispatch({ type: 'load' })
      void this.fetchStatus()
    })
    wrap.appendChild(retry)

    wrap.appendChild(this.makeReturnButton())
    this.container.appendChild(wrap)
  }

  private renderUnauthenticated(): void {
    const wrap = this.makeShell()
    const msg = document.createElement('div')
    msg.style.cssText = `text-align: center; color: ${NeonColors.textMuted}; margin: 24px 0;`
    msg.textContent = 'Войдите в Telegram, чтобы получать ежедневный бонус.'
    wrap.appendChild(msg)
    wrap.appendChild(this.makeReturnButton())
    this.container.appendChild(wrap)
  }

  // -----------------------------------------------------------------------
  // Components
  // -----------------------------------------------------------------------

  private makeShell(): HTMLDivElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'max-width: 420px; width: 92%; display: flex; flex-direction: column;'

    const header = document.createElement('div')
    header.style.cssText =
      'display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;'

    const title = document.createElement('h2')
    title.style.cssText = `
      ${neonTitleGlow()}
      font-size: clamp(1.4rem, 5.2vw, 1.9rem); margin: 0;
      letter-spacing: 0.02em;
    `
    title.textContent = '🎁 ЕЖЕДНЕВНЫЙ БОНУС'
    header.appendChild(title)

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.style.cssText = `
      background: transparent; border: none; color: #aaa;
      font-size: 1.5rem; cursor: pointer; padding: 4px 8px;
    `
    closeBtn.textContent = 'X'
    closeBtn.addEventListener('click', () => this.requestClose())
    header.appendChild(closeBtn)
    wrap.appendChild(header)

    return wrap
  }

  private makeStreakIndicator(streakDays: number): HTMLElement {
    const el = document.createElement('div')
    el.style.cssText = `
      margin-top: 6px; font-size: 1rem;
      color: ${NeonColors.gold}; font-weight: 600;
      text-shadow: 0 0 12px ${NeonColors.goldGlow};
    `
    el.textContent = `🔥 Streak: ${streakDays} дней подряд`
    return el
  }

  private makeBanner(text: string, accent: string): HTMLElement {
    const el = document.createElement('div')
    el.style.cssText = `
      margin-top: 12px; padding: 10px 14px;
      border-radius: 12px;
      background: rgba(0,2,10,0.55);
      border: 1px solid ${accent};
      color: ${accent}; font-size: 0.92rem; text-align: center;
      letter-spacing: 0.01em;
    `
    el.textContent = text
    return el
  }

  private makeGrid(status: DailyRewardStatus): HTMLElement {
    const cards = buildCardStates(status)
    const grid = document.createElement('div')
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-top: 18px;
    `

    // Row 1 — days 1..3 (3-column grid).
    for (let i = 0; i < 3; i++) {
      grid.appendChild(
        this.makeDayCard(i + 1, status.rewardSchedule[i], cards[i]),
      )
    }

    // Row 2 — days 4..7 — separate grid below with 4 columns.
    const row2 = document.createElement('div')
    row2.style.cssText = `
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 10px;
    `
    for (let i = 3; i < 7; i++) {
      row2.appendChild(
        this.makeDayCard(i + 1, status.rewardSchedule[i], cards[i]),
      )
    }

    // Wrap both rows in a container so this method returns a single node.
    const gridWrap = document.createElement('div')
    gridWrap.appendChild(grid)
    gridWrap.appendChild(row2)
    return gridWrap
  }

  private makeDayCard(day: number, reward: number, state: CardState): HTMLElement {
    const card = document.createElement('div')
    const isDay7 = day === 7

    let bg: string = 'rgba(255,255,255,0.04)'
    let border: string = '1px solid rgba(255,255,255,0.08)'
    let color: string = NeonColors.textBright
    let opacity = '1'
    let label = ''
    let extraShadow = ''

    switch (state) {
      case 'past_claimed':
        bg = 'rgba(0,255,136,0.05)'
        border = `1px solid rgba(0,255,136,0.25)`
        color = NeonColors.textMuted
        opacity = '0.85'
        label = '✓'
        break
      case 'today_claimable':
        bg = isDay7
          ? `rgba(255,215,0,0.12)`
          : `rgba(251,191,36,0.12)`
        border = `2px solid ${isDay7 ? NeonColors.gold : NeonColors.primary}`
        color = isDay7 ? NeonColors.gold : NeonColors.primary
        extraShadow = `box-shadow: 0 0 24px ${isDay7 ? NeonColors.goldGlow : NeonColors.primaryGlow};`
        card.classList.add('daily-pulse')
        break
      case 'today_claimed':
        bg = `rgba(0,255,136,0.12)`
        border = `1px solid ${NeonColors.success}`
        color = NeonColors.success
        label = '✓'
        break
      case 'future':
        opacity = '0.4'
        break
    }

    card.style.cssText = `
      background: ${bg};
      border: ${border};
      border-radius: 12px;
      padding: 12px 6px;
      display: flex; flex-direction: column; align-items: center;
      color: ${color}; opacity: ${opacity};
      ${extraShadow}
    `

    const dayLabel = document.createElement('div')
    dayLabel.style.cssText = `font-size: 0.75rem; letter-spacing: 0.05em; opacity: 0.85;`
    dayLabel.textContent = `Day ${day}`
    card.appendChild(dayLabel)

    const rewardLabel = document.createElement('div')
    rewardLabel.style.cssText = `
      font-size: ${isDay7 ? '1.25rem' : '1.05rem'};
      font-weight: 700;
      margin-top: 4px;
    `
    rewardLabel.textContent = `${reward}⭐`
    card.appendChild(rewardLabel)

    if (label) {
      const check = document.createElement('div')
      check.style.cssText = `font-size: 0.9rem; margin-top: 2px;`
      check.textContent = label
      card.appendChild(check)
    }

    return card
  }

  private makeClaimButton(reward: number, isClaiming: boolean): HTMLElement {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.style.cssText = this.primaryButtonCss()
    btn.disabled = isClaiming
    btn.textContent = isClaiming ? 'Забираем...' : `ЗАБРАТЬ ${reward}⭐`
    if (!isClaiming) btn.classList.add('daily-pulse')
    btn.addEventListener('click', () => {
      void this.doClaim()
    })
    return btn
  }

  private makeCountdown(nextRewardAvailableAt: string | null): HTMLElement {
    const el = document.createElement('div')
    el.style.cssText = `
      margin-top: 20px; padding: 14px 18px;
      border-radius: 14px; border: 1px solid rgba(255,255,255,0.12);
      text-align: center; color: ${NeonColors.textMuted};
      font-size: 1rem; font-family: ${FONTS.body};
    `
    el.textContent = `Возвращайся через ${formatCountdownText(nextRewardAvailableAt)}`
    return el
  }

  private makeReturnButton(): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.style.cssText = `
      margin-top: 28px; padding: 10px 18px;
      border-radius: 12px; border: 1px solid rgba(255,255,255,0.18);
      background: transparent; color: ${NeonColors.textMuted};
      font-size: 0.9rem; cursor: pointer; font-family: ${FONTS.body};
      align-self: center;
    `
    btn.textContent = '← Назад'
    btn.addEventListener('click', () => this.requestClose())
    return btn
  }

  private primaryButtonCss(): string {
    return `
      width: 100%; padding: 14px 18px; margin-top: 20px;
      border-radius: 14px; border: none;
      font-size: 1.05rem; font-weight: 700; letter-spacing: 0.04em;
      cursor: pointer; font-family: ${FONTS.body};
      background: ${NeonColors.primary}; color: #0f172a;
      box-shadow: 0 0 32px ${NeonColors.primaryGlow};
      transition: filter 0.15s;
    `
  }

  // -----------------------------------------------------------------------
  // Countdown timer (60s tick when grid shows a countdown)
  // -----------------------------------------------------------------------

  private syncCountdownTimer(): void {
    const needsTimer =
      this.state.kind === 'loaded' && !this.state.status.canClaim
    if (needsTimer && this.countdownTimer === null) {
      this.startCountdownTimer()
    } else if (!needsTimer && this.countdownTimer !== null) {
      this.stopCountdownTimer()
    }
  }

  private startCountdownTimer(): void {
    if (typeof window === 'undefined') return
    if (this.countdownTimer !== null) return
    this.countdownTimer = window.setInterval(() => {
      // Skip the re-render while the tab is hidden — countdown is purely
      // cosmetic, the user can't see it. Restored on the next tick after
      // the tab becomes visible.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      this.render()
    }, 60_000)
  }

  private stopCountdownTimer(): void {
    if (this.countdownTimer !== null && typeof window !== 'undefined') {
      window.clearInterval(this.countdownTimer)
    }
    this.countdownTimer = null
  }

  private requestClose(): void {
    this.onClose?.()
    this.hide()
  }
}

/**
 * Exported for unit testability — but the screen does not depend on a separate
 * helper module; keeping it here keeps the file ≤ 800 LOC.
 */
function formatCountdownText(nextRewardAvailableAt: string | null): string {
  if (!nextRewardAvailableAt) return '…'
  const target = Date.parse(nextRewardAvailableAt)
  if (Number.isNaN(target)) return '…'
  const remaining = Math.max(0, target - Date.now())
  const hours = Math.floor(remaining / 3_600_000)
  const minutes = Math.floor((remaining % 3_600_000) / 60_000)
  return `${pad2(hours)}:${pad2(minutes)}`
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}
